/**
 * Derive Garmin activity metrics from garmin_activity_details.samples_5s JSONB.
 * Used when garmin_activities summary columns are null (activity-files webhook path).
 */

function num(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function avg(values) {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function deriveActivityMetrics(samples) {
  if (!Array.isArray(samples) || samples.length === 0) {
    return null;
  }

  const times = [];
  const dists = [];
  const hrs = [];
  const powers = [];
  const speeds = [];

  for (const s of samples) {
    const t = num(s.t);
    if (t != null) times.push(t);
    const dist = num(s.dist);
    if (dist != null) dists.push(dist);
    const hr = num(s.hr);
    if (hr != null && hr > 0) hrs.push(hr);
    const power = num(s.power);
    if (power != null && power > 0) powers.push(power);
    const speed = num(s.speed);
    if (speed != null && speed > 0) speeds.push(speed);
  }

  const durationSeconds = times.length ? Math.max(...times) : null;
  const distanceMeters = dists.length ? Math.max(...dists) : null;
  const avgHeartRateBpm = hrs.length ? Math.round(avg(hrs)) : null;
  const maxHeartRateBpm = hrs.length ? Math.round(Math.max(...hrs)) : null;
  const avgPowerWatts = powers.length ? Math.round(avg(powers)) : null;
  const maxPowerWatts = powers.length ? Math.round(Math.max(...powers)) : null;
  const avgSpeedMps = speeds.length ? avg(speeds) : null;
  const avgPaceSecPerKm =
    avgSpeedMps != null && avgSpeedMps > 0 ? 1000 / avgSpeedMps : null;

  return {
    durationSeconds,
    distanceMeters,
    avgHeartRateBpm,
    maxHeartRateBpm,
    avgPowerWatts,
    maxPowerWatts,
    avgSpeedMps,
    avgPaceSecPerKm,
    sampleCount: samples.length,
  };
}

/** Prefer stored summary columns; fill gaps from samples. */
export function mergeActivityWithDerived(activity, samples) {
  const derived = deriveActivityMetrics(samples);

  return {
    ...activity,
    duration_seconds:
      num(activity.duration_seconds) ?? derived?.durationSeconds ?? null,
    distance_meters:
      num(activity.distance_meters) ?? derived?.distanceMeters ?? null,
    avg_heart_rate_bpm:
      num(activity.avg_heart_rate_bpm) ?? derived?.avgHeartRateBpm ?? null,
    max_heart_rate_bpm:
      num(activity.max_heart_rate_bpm) ?? derived?.maxHeartRateBpm ?? null,
    avg_speed_mps: num(activity.avg_speed_mps) ?? derived?.avgSpeedMps ?? null,
    avg_pace_sec_per_km:
      num(activity.avg_pace_sec_per_km) ?? derived?.avgPaceSecPerKm ?? null,
    avg_power_watts: derived?.avgPowerWatts ?? null,
    max_power_watts: derived?.maxPowerWatts ?? null,
    sampleCount: derived?.sampleCount ?? 0,
    hasSamples: Boolean(samples?.length),
  };
}

export function isRunningSport(sportType) {
  const n = String(sportType ?? '').toLowerCase();
  return n.includes('run') && !n.includes('cycling');
}

export function isCyclingSport(sportType) {
  const n = String(sportType ?? '').toLowerCase();
  return n.includes('cycl') || n.includes('bike') || n.includes('biking');
}

export function formatDuration(seconds) {
  if (seconds == null) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatPace(secPerKm) {
  if (secPerKm == null || secPerKm <= 0) return null;
  const mins = Math.floor(secPerKm / 60);
  const secs = Math.round(secPerKm % 60);
  return `${mins}:${String(secs).padStart(2, '0')} /km`;
}

export function formatSpeedKmh(mps) {
  if (mps == null || mps <= 0) return null;
  return `${(mps * 3.6).toFixed(1)} km/h`;
}

export function formatSportType(sportType) {
  return sportType?.replace(/_/g, ' ') || 'Activity';
}

/** Chart-ready rows: time in minutes + optional HR, power, speed. */
export function samplesToChartData(samples) {
  if (!Array.isArray(samples)) return [];
  return samples
    .map((s) => {
      const t = num(s.t);
      if (t == null) return null;
      return {
        timeMin: Math.round((t / 60) * 10) / 10,
        hr: num(s.hr),
        power: num(s.power),
        speedKmh: num(s.speed) != null ? num(s.speed) * 3.6 : null,
        distKm: num(s.dist) != null ? num(s.dist) / 1000 : null,
      };
    })
    .filter(Boolean);
}
